import { c as _c } from "react-compiler-runtime";
import { createContext, forwardRef, useContext, useMemo } from "react";
import { FieldValues, Path, useController } from "react-hook-form";
import { ErrorMessage, HintMessage, Label, useFieldErrorVisibility, useFieldIds } from "@/components/form";
import { Input as UiInput } from "@/components/ui/input";
import { cn } from "@/core/utils";
import { SIGNUP_FIELD_INPUT_CLASSNAME, SIGNUP_FIELD_LABEL_CLASSNAME } from "@/features/signup/components/shared/signupFormStyles";
type FieldAppearance = "default" | "signup";
type TextFieldContextValue = {
  inputId: string;
  labelId: string;
  hintId: string;
  errorId: string;
};
const TextFieldContext = createContext<TextFieldContextValue | null>(null);
const FieldAppearanceContext = createContext<FieldAppearance>("default");
function useTextFieldContext() {
  const ctx = useContext(TextFieldContext);
  if (!ctx) {
    throw new globalThis.Error("TextField components must be used within TextField.Root");
  }
  return ctx;
}
function useFieldAppearance() {
  return useContext(FieldAppearanceContext);
}
type TextLabelProps = {
  text: string;
};
function TextLabel(t0) {
  const $ = _c(5);
  const {
    text
  } = t0;
  const {
    inputId,
    labelId
  } = useTextFieldContext();
  const appearance = useFieldAppearance();
  const t1 = appearance === "signup" ? SIGNUP_FIELD_LABEL_CLASSNAME : undefined;
  let t2;
  if ($[0] !== inputId || $[1] !== labelId || $[2] !== t1 || $[3] !== text) {
    t2 = <Label className={t1} htmlFor={inputId} id={labelId} text={text} />;
    $[0] = inputId;
    $[1] = labelId;
    $[2] = t1;
    $[3] = text;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
type TextHintProps = {
  text: string;
};
function TextHint(t0) {
  const $ = _c(3);
  const {
    text
  } = t0;
  const {
    hintId
  } = useTextFieldContext();
  let t1;
  if ($[0] !== hintId || $[1] !== text) {
    t1 = <HintMessage id={hintId} text={text} />;
    $[0] = hintId;
    $[1] = text;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type TextErrorMessageProps = {
  text: string;
};
function TextErrorMessage(t0) {
  const $ = _c(4);
  const {
    text
  } = t0;
  const {
    inputId,
    errorId
  } = useTextFieldContext();
  let t1;
  if ($[0] !== errorId || $[1] !== inputId || $[2] !== text) {
    t1 = <ErrorMessage htmlFor={inputId} id={errorId} text={text} />;
    $[0] = errorId;
    $[1] = inputId;
    $[2] = text;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
type RootProps = {
  appearance?: FieldAppearance;
  children: React.ReactNode;
  name: string;
};
function Root(t0) {
  const $ = _c(16);
  const {
    appearance: t1,
    children,
    name
  } = t0;
  const appearance = t1 === undefined ? "default" : t1;
  const fieldIds = useFieldIds(name);
  let t2;
  if ($[0] !== fieldIds.errorId || $[1] !== fieldIds.hintId || $[2] !== fieldIds.inputId || $[3] !== fieldIds.labelId) {
    t2 = {
      inputId: fieldIds.inputId,
      labelId: fieldIds.labelId,
      hintId: fieldIds.hintId,
      errorId: fieldIds.errorId
    };
    $[0] = fieldIds.errorId;
    $[1] = fieldIds.hintId;
    $[2] = fieldIds.inputId;
    $[3] = fieldIds.labelId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const value = t2;
  const t3 = appearance === "signup" ? "gap-[var(--signup-field-label-gap,8px)]" : "gap-1";
  let t4;
  if ($[5] !== t3) {
    t4 = cn("flex flex-col text-left", t3);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children || $[8] !== t4) {
    t5 = <div className={t4}>{children}</div>;
    $[7] = children;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5 || $[11] !== value) {
    t6 = <TextFieldContext.Provider value={value}>{t5}</TextFieldContext.Provider>;
    $[10] = t5;
    $[11] = value;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== appearance || $[14] !== t6) {
    t7 = <FieldAppearanceContext.Provider value={appearance}>{t6}</FieldAppearanceContext.Provider>;
    $[13] = appearance;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
type NativeProps = {
  appearance?: FieldAppearance;
  label: string;
  hint?: string;
  inputProps: InputProps;
  name: string;
};
function Native(t0) {
  const $ = _c(12);
  const {
    appearance: t1,
    label,
    hint,
    inputProps,
    name
  } = t0;
  const appearance = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] !== label) {
    t2 = <TextLabel text={label} />;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== inputProps) {
    t3 = <Input {...inputProps} />;
    $[2] = inputProps;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== hint) {
    t4 = hint && <TextHint text={hint} />;
    $[4] = hint;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== appearance || $[7] !== name || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <Root appearance={appearance} name={name}>{t2}{t3}{t4}</Root>;
    $[6] = appearance;
    $[7] = name;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
type InputProps = React.InputHTMLAttributes<HTMLInputElement>;
const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const $ = _c(21);
  const {
    errorId,
    hintId,
    inputId,
    labelId
  } = useTextFieldContext();
  const appearance = useFieldAppearance();
  let className;
  let onBlur;
  let onChange;
  let rest;
  if ($[0] !== props) {
    const {
      id: _id,
      className: t0,
      onBlur: t1,
      onChange: t2,
      ...t3
    } = props;
    className = t0;
    onBlur = t1;
    onChange = t2;
    rest = t3;
    $[0] = props;
    $[1] = className;
    $[2] = onBlur;
    $[3] = onChange;
    $[4] = rest;
  } else {
    className = $[1];
    onBlur = $[2];
    onChange = $[3];
    rest = $[4];
  }
  const invalid = !!props["aria-invalid"];
  const t0 = `${hintId} ${errorId}`;
  const t1 = props["aria-errormessage"];
  const t2 = invalid || undefined;
  const t3 = appearance === "signup" ? SIGNUP_FIELD_INPUT_CLASSNAME : "peer border-input bg-card text-foreground placeholder:text-muted-foreground/60 focus:ring-ring type-body-md block h-12 w-full appearance-none rounded border px-4 py-3 ring-offset-1 outline-none focus:ring-1 disabled:pointer-events-none disabled:opacity-50";
  const t4 = appearance === "signup" ? "block w-full disabled:pointer-events-none" : undefined;
  const t5 = invalid && "border-destructive ring-destructive placeholder:text-destructive ring-1";
  let t6;
  if ($[5] !== className || $[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t6 = cn(t3, t4, t5, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== inputId || $[11] !== labelId || $[12] !== onBlur || $[13] !== onChange || $[14] !== ref || $[15] !== rest || $[16] !== t0 || $[17] !== t1 || $[18] !== t2 || $[19] !== t6) {
    t7 = <UiInput {...rest} aria-describedby={t0} aria-errormessage={t1} aria-invalid={t2} aria-labelledby={labelId} className={t6} id={inputId} ref={ref} onBlur={onBlur} onChange={onChange} />;
    $[10] = inputId;
    $[11] = labelId;
    $[12] = onBlur;
    $[13] = onChange;
    $[14] = ref;
    $[15] = rest;
    $[16] = t0;
    $[17] = t1;
    $[18] = t2;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
});
Input.displayName = "TextField.Input";
type RHF<T extends FieldValues> = {
  appearance?: FieldAppearance;
  name: Path<T>;
  label: string;
  hint?: string;
  inputProps?: Omit<InputProps, "id" | "name" | "value" | "defaultValue" | "aria-invalid" | "aria-errormessage">;
};
function RHF(t0) {
  const $ = _c(41);
  const {
    appearance: t1,
    name,
    label,
    hint,
    inputProps
  } = t0;
  const appearance = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] !== name) {
    t2 = {
      name
    };
    $[0] = name;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    field,
    fieldState
  } = useController(t2);
  const showError = useFieldErrorVisibility(fieldState);
  let t3;
  if ($[2] !== inputProps) {
    t3 = inputProps ?? {};
    $[2] = inputProps;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let onBlurProp;
  let onChangeProp;
  let restInputProps;
  if ($[4] !== t3) {
    ({
      onChange: onChangeProp,
      onBlur: onBlurProp,
      ...restInputProps
    } = t3);
    $[4] = t3;
    $[5] = onBlurProp;
    $[6] = onChangeProp;
    $[7] = restInputProps;
  } else {
    onBlurProp = $[5];
    onChangeProp = $[6];
    restInputProps = $[7];
  }
  let t4;
  if ($[8] !== field || $[9] !== onChangeProp) {
    t4 = e => {
      onChangeProp?.(e);
      field.onChange(e);
    };
    $[8] = field;
    $[9] = onChangeProp;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleChange = t4;
  let t5;
  if ($[11] !== field || $[12] !== onBlurProp) {
    t5 = e_0 => {
      onBlurProp?.(e_0);
      field.onBlur();
    };
    $[11] = field;
    $[12] = onBlurProp;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleBlur = t5;
  let t6;
  if ($[14] !== label) {
    t6 = <TextLabel text={label} />;
    $[14] = label;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = fieldState.error?.message;
  const t8 = showError || undefined;
  const t9 = typeof field.value === "string" ? field.value : field.value ?? "";
  let t10;
  if ($[16] !== field.name || $[17] !== handleBlur || $[18] !== handleChange || $[19] !== restInputProps || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t10 = <Input aria-errormessage={t7} aria-invalid={t8} {...restInputProps} name={field.name} value={t9} onBlur={handleBlur} onChange={handleChange} />;
    $[16] = field.name;
    $[17] = handleBlur;
    $[18] = handleChange;
    $[19] = restInputProps;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== hint) {
    t11 = hint && <TextHint text={hint} />;
    $[24] = hint;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = showError ? "pt-1" : appearance === "signup" ? undefined : "h-2";
  let t13;
  if ($[26] !== t12) {
    t13 = cn(t12);
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== fieldState.error || $[29] !== showError) {
    t14 = showError && fieldState.error?.message ? <TextErrorMessage text={fieldState.error.message} /> : null;
    $[28] = fieldState.error;
    $[29] = showError;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t13 || $[32] !== t14) {
    t15 = <div className={t13}>{t14}</div>;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== appearance || $[35] !== name || $[36] !== t10 || $[37] !== t11 || $[38] !== t15 || $[39] !== t6) {
    t16 = <Root appearance={appearance} name={name}>{t6}{t10}{t11}{t15}</Root>;
    $[34] = appearance;
    $[35] = name;
    $[36] = t10;
    $[37] = t11;
    $[38] = t15;
    $[39] = t6;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
}
export const TextField = {
  RHF,
  Native
};